export const isPaidProductOrder = (order) => order?.paymentStatus === 'پرداخت شده';

export const canDownloadProductOrder = (order) => (
  isPaidProductOrder(order)
  && !['لغو شده', 'بازپرداخت شده'].includes(order?.status)
  && Boolean(order?.product?.downloadFile?.originalName)
);

export const isPaidServiceOrder = (order) => order?.paymentStatus === 'paid';
export const getServicePaidAmount = (order) => {
  const scheduledPaid = Number(order?.paymentSchedule?.paidAmount);
  if (Number.isFinite(scheduledPaid) && scheduledPaid > 0) return scheduledPaid;
  return isPaidServiceOrder(order) ? Number(order?.quote?.amount) || 0 : 0;
};

export const getCombinedDashboardSummary = (productOrders = [], serviceOrders = []) => {
  const paidProductOrders = productOrders.filter(isPaidProductOrder);
  const paidServiceOrders = serviceOrders.filter((order) => getServicePaidAmount(order) > 0);
  const productFiles = productOrders.filter(canDownloadProductOrder).length;
  const serviceFiles = serviceOrders.reduce(
    (total, order) => total + (Array.isArray(order?.attachments) ? order.attachments.length : 0),
    0
  );

  const spentByCurrency = new Map();
  const addSpent = (order, amount) => {
    const currency = order?.currencyLabel || order?.paymentSchedule?.currency || 'تومان';
    spentByCurrency.set(currency, (spentByCurrency.get(currency) || 0) + (Number(amount) || 0));
  };
  paidProductOrders.forEach((order) => addSpent(order, order.amount));
  serviceOrders.forEach((order) => {
    const amount = getServicePaidAmount(order);
    if (amount > 0) addSpent(order, amount);
  });
  return {
    total: productOrders.length + serviceOrders.length,
    paid: paidProductOrders.length + paidServiceOrders.length,
    downloadable: productFiles + serviceFiles,
    totalSpent: paidProductOrders.reduce(
      (total, order) => total + (Number(order?.amount) || 0),
      0
    ) + serviceOrders.reduce(
      (total, order) => total + getServicePaidAmount(order),
      0
    ),
    spentByCurrency: [...spentByCurrency.entries()].map(([currency, amount]) => ({ currency, amount })),
  };
};

export const getRecentCombinedOrders = (productOrders = [], serviceOrders = [], limit = 3) => (
  [
    ...productOrders.map((order) => ({ type: 'product', order })),
    ...serviceOrders.map((order) => ({ type: 'service', order })),
  ]
    .sort((left, right) => (
      new Date(right.order?.createdAt || 0).getTime() - new Date(left.order?.createdAt || 0).getTime()
    ))
    .slice(0, limit)
);

const DAY_MS = 24 * 60 * 60 * 1000;

export const getSupportSubscriptionTiming = (order, now = new Date()) => {
  const subscription = order?.supportSubscription;
  if (!subscription?.startsAt || !subscription?.endsAt) return null;
  const startsAt = new Date(subscription.startsAt);
  const endsAt = new Date(subscription.endsAt);
  if (Number.isNaN(startsAt.getTime()) || Number.isNaN(endsAt.getTime())) return null;

  const requestedTime = new Date(now).getTime();
  const currentTime = Number.isNaN(requestedTime) ? Date.now() : requestedTime;
  const totalDays = Math.max(1, Math.ceil((endsAt.getTime() - startsAt.getTime()) / DAY_MS));
  const daysRemaining = Math.max(0, Math.ceil((endsAt.getTime() - currentTime) / DAY_MS));
  const remainingPercent = Math.max(0, Math.min(100, Math.round((daysRemaining / totalDays) * 100)));
  let status = subscription.status || 'active';
  if (!['cancelled', 'suspended'].includes(status)) {
    status = currentTime >= endsAt.getTime()
      ? 'expired'
      : currentTime < startsAt.getTime()
        ? 'inactive'
        : 'active';
  }

  return {
    ...subscription,
    status,
    startsAt: startsAt.toISOString(),
    endsAt: endsAt.toISOString(),
    totalDays,
    daysRemaining,
    remainingPercent,
    order,
  };
};

export const getSupportSubscriptionCards = (serviceOrders = [], now = new Date()) => (
  serviceOrders
    .map((order) => getSupportSubscriptionTiming(order, now))
    .filter(Boolean)
    .sort((left, right) => {
      const rank = { active: 0, inactive: 1, suspended: 2, expired: 3, cancelled: 4 };
      const statusDifference = (rank[left.status] ?? 9) - (rank[right.status] ?? 9);
      if (statusDifference) return statusDifference;
      return new Date(right.endsAt).getTime() - new Date(left.endsAt).getTime();
    })
);
